import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import type { AuditRow } from '../lib/types';
import { formatDateTime } from '../lib/utils';

/** Журнал аудита: кто, что и когда изменил (append-only). */
export function AuditPage() {
  const { t, i18n } = useTranslation();
  const [entityType, setEntityType] = useState('');
  const [entityId, setEntityId] = useState('');
  const filter = new URLSearchParams({ limit: '100' });
  if (entityType.trim()) filter.set('entityType', entityType.trim());
  if (/^[0-9a-f-]{36}$/i.test(entityId.trim())) filter.set('entityId', entityId.trim());
  const logs = useInfiniteQuery({
    queryKey: ['audit', filter.toString()],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams(filter);
      if (pageParam) params.set('before', pageParam);
      return api<{ items: AuditRow[]; nextBefore: string | null }>(
        `/audit-logs?${params.toString()}`,
      );
    },
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
  const rows = logs.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.hint')} />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input
          aria-label={t('audit.entityType')}
          placeholder={t('audit.entityType')}
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
          }}
          className="w-48"
        />
        <Input
          aria-label={t('audit.entityId')}
          placeholder={t('audit.entityId')}
          value={entityId}
          onChange={(e) => {
            setEntityId(e.target.value);
          }}
          className="w-80 font-mono"
        />
      </div>
      {logs.error ? <Alert variant="destructive">{errorMessage(logs.error)}</Alert> : null}
      <div className="divide-y rounded-md border text-sm">
        {rows.map((r) => (
          <details key={r.id} className="p-3">
            <summary className="flex cursor-pointer flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {formatDateTime(r.ts, i18n.language)}
              </span>
              <Badge variant="outline">{r.actorType}</Badge>
              <span className="font-medium">{r.action}</span>
              <span className="text-muted-foreground">
                {r.entityType}
                {r.entityId ? ` ${r.entityId.slice(0, 8)}…` : ''}
              </span>
            </summary>
            <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
              {JSON.stringify({ actorId: r.actorId, before: r.before, after: r.after }, null, 2)}
            </pre>
          </details>
        ))}
      </div>
      {logs.hasNextPage ? (
        <Button
          variant="outline"
          className="mt-3"
          disabled={logs.isFetchingNextPage}
          onClick={() => void logs.fetchNextPage()}
        >
          {t('audit.more')}
        </Button>
      ) : null}
    </>
  );
}
